import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  BarChart,
  CheckCircle,
  Settings,
  Lock,
  Loader2,
  Check,
} from 'lucide-react';
import Layout from '../components/Layout';
import clientService from '../services/clientService';
import authService from '../services/authService';

const ClientOnboardingPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Check if current user is admin
  const isAdmin = authService.isAdmin();

  // Auto-hide notification
  useEffect(() => {
    if (notification.open) {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, open: false }));
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [notification.open]);

  // Form data state
  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    client_id: '',
    client_name: '',
    status: 'active',
    is_confidential: false,

    // Step 2: Contact Information
    contact_info: {
      primary_contact: '',
      email: '',
      phone: '',
      address: ''
    },

    // Step 3: Business Information
    business_info: {
      industry: '',
      company_size: '',
      credit_rating: ''
    },

    // Step 4: Preferences & Settings
    preferences: {
      preferred_currency: 'CNY',
      payment_terms: '',
      discount_tier: 'standard',
      tax_rate: 0.13
    }
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  // Step definitions
  const steps = [
    {
      label: '基本信息',
      description: '设置客户的基本标识信息'
    },
    {
      label: '联系信息',
      description: '添加联系人和联系方式'
    },
    {
      label: '企业信息',
      description: '配置企业分类和规模信息'
    },
    {
      label: '商务设置',
      description: '设置商务偏好和默认参数'
    },
    {
      label: '完成设置',
      description: '确认信息并创建客户'
    }
  ];

  // Form validation for each step
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Basic Information
        if (!formData.client_id.trim()) {
          newErrors.client_id = '客户编号为必填项';
        }
        if (!formData.client_name.trim()) {
          newErrors.client_name = '客户名称为必填项';
        }
        break;

      case 1: // Contact Information
        if (!formData.contact_info.primary_contact.trim()) {
          newErrors.primary_contact = '主要联系人为必填项';
        }
        if (formData.contact_info.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_info.email)) {
          newErrors.email = '请输入有效的邮箱地址';
        }
        if (formData.contact_info.phone && !/^(\+86)?[1-9]\d{10}$/.test(formData.contact_info.phone.replace(/[\s-]/g, ''))) {
          newErrors.phone = '请输入有效的手机号码';
        }
        break;

      case 2: // Business Information
        // Optional validations for business info
        break;

      case 3: // Preferences
        if (formData.preferences.tax_rate < 0 || formData.preferences.tax_rate > 1) {
          newErrors.tax_rate = '税率应在0-1之间';
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value, nested = null) => {
    if (nested) {
      setFormData(prev => ({
        ...prev,
        [nested]: {
          ...prev[nested],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when field is updated
    const errorKey = nested ? field : field;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: undefined
      }));
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setCompleted(prev => ({ ...prev, [activeStep]: true }));
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleStepClick = (step) => {
    if (step < activeStep || completed[step]) {
      setActiveStep(step);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await clientService.createClient(formData);

      setNotification({
        open: true,
        message: '客户创建成功！正在跳转到客户详情页面...',
        severity: 'success'
      });

      // Navigate to client detail page after a short delay
      setTimeout(() => {
        if (response.client && response.client._id) {
          navigate(`/settings/clients/${response.client._id}`);
        } else {
          navigate('/settings/clients');
        }
      }, 2000);

    } catch (error) {
      console.error('Error creating client:', error);
      setNotification({
        open: true,
        message: error.response?.data?.message || '创建客户失败，请重试',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
    setFormData({
      client_id: '',
      client_name: '',
      status: 'active',
      is_confidential: false,
      contact_info: {
        primary_contact: '',
        email: '',
        phone: '',
        address: ''
      },
      business_info: {
        industry: '',
        company_size: '',
        credit_rating: ''
      },
      preferences: {
        preferred_currency: 'CNY',
        payment_terms: '',
        discount_tier: 'standard',
        tax_rate: 0.13
      }
    });
    setErrors({});
  };

  // Option arrays
  const industryOptions = [
    '汽车制造', '电子制造', '机械制造', '化工', '医疗器械',
    '食品加工', '纺织', '建材', '其他'
  ];

  const companySizeOptions = [
    { value: 'small', label: '小型企业 (<100人)' },
    { value: 'medium', label: '中型企业 (100-500人)' },
    { value: 'large', label: '大型企业 (>500人)' }
  ];

  const creditRatingOptions = ['AAA', 'AA', 'A', 'BBB', 'BB', 'B', 'CCC', 'CC', 'C'];

  const discountTierOptions = [
    { value: 'gold', label: '金牌客户 (5-10%)' },
    { value: 'silver', label: '银牌客户 (3-5%)' },
    { value: 'bronze', label: '铜牌客户 (1-3%)' },
    { value: 'standard', label: '标准客户 (0-1%)' }
  ];

  const paymentTermsOptions = [
    '现金', '15天付款', '30天付款', '45天付款', '60天付款',
    '90天付款', '月结', '季结'
  ];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <Alert className="border-info/50 bg-info/10">
              <AlertDescription className="text-info">
                请填写客户的基本标识信息。客户编号创建后无法修改，请仔细填写。
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_id">客户编号 *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="client_id"
                    value={formData.client_id}
                    onChange={(e) => handleInputChange('client_id', e.target.value)}
                    className={cn("pl-9", errors.client_id && "border-destructive")}
                    placeholder="例如: CLIENT_001"
                  />
                </div>
                {errors.client_id ? (
                  <p className="text-sm text-destructive">{errors.client_id}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">例如: CLIENT_001 或 COMP_ABC</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_name">客户名称 *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => handleInputChange('client_name', e.target.value)}
                    className={cn("pl-9", errors.client_name && "border-destructive")}
                  />
                </div>
                {errors.client_name && (
                  <p className="text-sm text-destructive">{errors.client_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>初始状态</Label>
                <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">活跃</SelectItem>
                    <SelectItem value="pending">待审核</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">新客户通常设置为待审核状态</p>
              </div>
            </div>

            {isAdmin && (
              <Card className="border bg-muted/50 mt-4">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Lock className={cn("h-5 w-5", formData.is_confidential ? "text-warning" : "text-muted-foreground")} />
                      <div>
                        <p className="font-medium">客户保密性设置</p>
                        <p className="text-sm text-muted-foreground">
                          标记为保密客户后，仅管理员可以查看该客户及相关数据
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{formData.is_confidential ? "保密客户" : "普通客户"}</span>
                      <Switch
                        checked={formData.is_confidential}
                        onCheckedChange={(checked) => handleInputChange('is_confidential', checked)}
                      />
                    </div>
                  </div>
                  {formData.is_confidential && (
                    <Alert className="border-warning/50 bg-warning/10 mt-4">
                      <AlertDescription className="text-warning">
                        <strong>注意：</strong>启用保密模式后：
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>只有管理员可以查看此客户</li>
                          <li>非管理员用户无法在客户列表中看到此客户</li>
                          <li>此客户的所有报价单、销售订单和采购订单将被隐藏</li>
                          <li>此客户不会出现在非管理员的搜索结果中</li>
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <Alert className="border-info/50 bg-info/10">
              <AlertDescription className="text-info">
                请填写主要联系人信息。至少需要填写联系人姓名。
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primary_contact">主要联系人 *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="primary_contact"
                    value={formData.contact_info.primary_contact}
                    onChange={(e) => handleInputChange('primary_contact', e.target.value, 'contact_info')}
                    className={cn("pl-9", errors.primary_contact && "border-destructive")}
                  />
                </div>
                {errors.primary_contact && (
                  <p className="text-sm text-destructive">{errors.primary_contact}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">邮箱地址</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.contact_info.email}
                    onChange={(e) => handleInputChange('email', e.target.value, 'contact_info')}
                    className={cn("pl-9", errors.email && "border-destructive")}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">联系电话</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    value={formData.contact_info.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value, 'contact_info')}
                    className={cn("pl-9", errors.phone && "border-destructive")}
                  />
                </div>
                {errors.phone ? (
                  <p className="text-sm text-destructive">{errors.phone}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">格式: +86-138-0000-0000</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">地址</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Textarea
                    id="address"
                    value={formData.contact_info.address}
                    onChange={(e) => handleInputChange('address', e.target.value, 'contact_info')}
                    className="pl-9 min-h-[80px]"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <Alert className="border-info/50 bg-info/10">
              <AlertDescription className="text-info">
                企业信息帮助系统更好地了解客户业务背景，便于后续服务。
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>行业分类</Label>
                <Select
                  value={formData.business_info.industry}
                  onValueChange={(value) => handleInputChange('industry', value, 'business_info')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择行业" />
                  </SelectTrigger>
                  <SelectContent>
                    {industryOptions.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>企业规模</Label>
                <Select
                  value={formData.business_info.company_size}
                  onValueChange={(value) => handleInputChange('company_size', value, 'business_info')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择规模" />
                  </SelectTrigger>
                  <SelectContent>
                    {companySizeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>信用等级</Label>
                <div className="relative">
                  <BarChart className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <Select
                    value={formData.business_info.credit_rating}
                    onValueChange={(value) => handleInputChange('credit_rating', value, 'business_info')}
                  >
                    <SelectTrigger className="pl-9">
                      <SelectValue placeholder="选择等级" />
                    </SelectTrigger>
                    <SelectContent>
                      {creditRatingOptions.map((rating) => (
                        <SelectItem key={rating} value={rating}>
                          {rating}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <Alert className="border-info/50 bg-info/10">
              <AlertDescription className="text-info">
                商务设置将影响该客户的默认定价、付款条件等。后续可以在客户详情中修改。
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>首选货币</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <Select
                    value={formData.preferences.preferred_currency}
                    onValueChange={(value) => handleInputChange('preferred_currency', value, 'preferences')}
                  >
                    <SelectTrigger className="pl-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CNY">人民币 (CNY)</SelectItem>
                      <SelectItem value="USD">美元 (USD)</SelectItem>
                      <SelectItem value="EUR">欧元 (EUR)</SelectItem>
                      <SelectItem value="JPY">日元 (JPY)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>付款条件</Label>
                <Select
                  value={formData.preferences.payment_terms}
                  onValueChange={(value) => handleInputChange('payment_terms', value, 'preferences')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择付款条件" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentTermsOptions.map((term) => (
                      <SelectItem key={term} value={term}>
                        {term}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>折扣等级</Label>
                <Select
                  value={formData.preferences.discount_tier}
                  onValueChange={(value) => handleInputChange('discount_tier', value, 'preferences')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {discountTierOptions.map((tier) => (
                      <SelectItem key={tier.value} value={tier.value}>
                        {tier.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tax_rate">默认税率</Label>
                <div className="relative">
                  <Input
                    id="tax_rate"
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={formData.preferences.tax_rate}
                    onChange={(e) => handleInputChange('tax_rate', parseFloat(e.target.value) || 0, 'preferences')}
                    className={cn(errors.tax_rate && "border-destructive")}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                </div>
                {errors.tax_rate ? (
                  <p className="text-sm text-destructive">{errors.tax_rate}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">输入小数形式，如0.13表示13%</p>
                )}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <Alert className="border-success/50 bg-success/10">
              <CheckCircle className="h-4 w-4 text-success" />
              <AlertDescription className="text-success ml-2">
                信息填写完成！请确认以下信息无误后点击"创建客户"。
              </AlertDescription>
            </Alert>

            {/* Summary of all information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border">
                <CardContent className="p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    基本信息
                  </h4>
                  <Separator className="mb-3" />
                  <div className="space-y-2 text-sm">
                    <p><strong>客户编号:</strong> {formData.client_id}</p>
                    <p><strong>客户名称:</strong> {formData.client_name}</p>
                    <p><strong>状态:</strong> {formData.status === 'active' ? '活跃' : '待审核'}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border">
                <CardContent className="p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    联系信息
                  </h4>
                  <Separator className="mb-3" />
                  <div className="space-y-2 text-sm">
                    <p><strong>联系人:</strong> {formData.contact_info.primary_contact || '-'}</p>
                    <p><strong>邮箱:</strong> {formData.contact_info.email || '-'}</p>
                    <p><strong>电话:</strong> {formData.contact_info.phone || '-'}</p>
                    <p><strong>地址:</strong> {formData.contact_info.address || '-'}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border">
                <CardContent className="p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <BarChart className="h-4 w-4" />
                    企业信息
                  </h4>
                  <Separator className="mb-3" />
                  <div className="space-y-2 text-sm">
                    <p><strong>行业:</strong> {formData.business_info.industry || '-'}</p>
                    <p><strong>规模:</strong> {
                      formData.business_info.company_size === 'small' ? '小型企业' :
                      formData.business_info.company_size === 'medium' ? '中型企业' :
                      formData.business_info.company_size === 'large' ? '大型企业' : '-'
                    }</p>
                    <p><strong>信用等级:</strong> {formData.business_info.credit_rating || '-'}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border">
                <CardContent className="p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    商务设置
                  </h4>
                  <Separator className="mb-3" />
                  <div className="space-y-2 text-sm">
                    <p><strong>货币:</strong> {formData.preferences.preferred_currency}</p>
                    <p><strong>付款:</strong> {formData.preferences.payment_terms || '-'}</p>
                    <p><strong>折扣:</strong> {
                      formData.preferences.discount_tier === 'gold' ? '金牌客户' :
                      formData.preferences.discount_tier === 'silver' ? '银牌客户' :
                      formData.preferences.discount_tier === 'bronze' ? '铜牌客户' :
                      formData.preferences.discount_tier === 'standard' ? '标准客户' : '-'
                    }</p>
                    <p><strong>税率:</strong> {(formData.preferences.tax_rate * 100).toFixed(1)}%</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout title="客户入驻向导" activeTab="settings">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
        {/* Notification Toast */}
        {notification.open && (
          <div className="fixed top-4 right-4 z-50">
            <Alert
              variant={notification.severity === 'error' ? 'destructive' : 'default'}
              className={cn(
                'shadow-lg',
                notification.severity === 'success' && 'border-success/50 bg-success/10'
              )}
            >
              <AlertDescription className={cn(
                notification.severity === 'success' && 'text-success'
              )}>
                {notification.message}
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-1">
            客户入驻向导
          </h1>
          <p className="text-muted-foreground">
            通过分步向导快速创建和配置新客户
          </p>
        </div>

        <Card className="border bg-card">
          <CardContent className="p-6">
            {/* Custom Stepper */}
            <div className="space-y-6">
              {steps.map((step, index) => (
                <div key={step.label}>
                  {/* Step Header */}
                  <div
                    className={cn(
                      "flex items-center gap-4 cursor-pointer",
                      (index > activeStep && !completed[index]) && "cursor-not-allowed opacity-50"
                    )}
                    onClick={() => handleStepClick(index)}
                  >
                    {/* Step Circle */}
                    <div className={cn(
                      "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors",
                      completed[index] ? "bg-primary border-primary text-primary-foreground" :
                      index === activeStep ? "border-primary text-primary" :
                      "border-muted-foreground/30 text-muted-foreground"
                    )}>
                      {completed[index] ? <Check className="h-4 w-4" /> : index + 1}
                    </div>

                    {/* Step Label */}
                    <div>
                      <p className={cn(
                        "font-semibold",
                        index === activeStep ? "text-foreground" : "text-muted-foreground"
                      )}>
                        {step.label}
                      </p>
                    </div>
                  </div>

                  {/* Step Content */}
                  {index === activeStep && (
                    <div className="ml-12 mt-4">
                      <p className="text-sm text-muted-foreground mb-4">
                        {step.description}
                      </p>

                      {renderStepContent(index)}

                      <div className="mt-6 flex gap-2">
                        {index === steps.length - 1 ? (
                          <Button onClick={handleSubmit} disabled={loading}>
                            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {loading ? '创建中...' : '创建客户'}
                          </Button>
                        ) : (
                          <Button onClick={handleNext}>
                            下一步
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          disabled={index === 0}
                          onClick={handleBack}
                        >
                          上一步
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div className={cn(
                      "ml-4 border-l-2 h-4",
                      completed[index] ? "border-primary" : "border-muted-foreground/30"
                    )} />
                  )}
                </div>
              ))}
            </div>

            {/* Reset button at the bottom */}
            {activeStep > 0 && (
              <div className="mt-6 text-center">
                <Button onClick={handleReset} variant="outline" className="text-destructive border-destructive hover:bg-destructive/10">
                  重新开始
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ClientOnboardingPage;
